package dev.shaughnessy.knittingcolorpicker.knittingcolorpicker

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeContentPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.Checkbox
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuAnchorType
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.ExposedDropdownMenuDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Slider
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.luminance
import androidx.compose.ui.unit.dp
import com.github.skydoves.colorpicker.compose.BrightnessSlider
import com.github.skydoves.colorpicker.compose.ColorEnvelope
import com.github.skydoves.colorpicker.compose.HsvColorPicker
import com.github.skydoves.colorpicker.compose.rememberColorPickerController
import kotlinx.coroutines.launch

@OptIn(ExperimentalLayoutApi::class, ExperimentalMaterial3Api::class)
@Composable
fun App() {
    MaterialTheme {
        var sliderState by remember { mutableStateOf(2f) }
        val colorCount = sliderState.toInt()

        // Initialize with 20 colors
        val colors = remember { mutableStateListOf<Color>().apply {
            repeat(20) { add(Color.LightGray) }
        }}

        // Track which boxes have been set
        val setIndices = remember { mutableStateMapOf<Int, Boolean>() }
        // Track assigned numbers
        val assignedNumbers = remember { mutableStateMapOf<Int, Int>() }
        // Track manually assigned numbers
        val manualNumbers = remember { mutableStateMapOf<Int, Int>() }
        // Track assigned row heights
        val assignedRowHeights = remember { mutableStateMapOf<Int, Int>() }
        // Track selected row height options
        val selectedRowHeightOptions = remember { mutableStateMapOf<Int, Boolean>() }
        
        // Track stripe preview visibility
        var showStripePreview by remember { mutableStateOf(false) }

        // Reset assigned numbers when count changes
        LaunchedEffect(colorCount) {
            assignedNumbers.clear()
            manualNumbers.clear()
            assignedRowHeights.clear()
            showStripePreview = false
        }

        Row(
            modifier = Modifier
                .background(MaterialTheme.colorScheme.primaryContainer)
                .safeContentPadding()
                .fillMaxSize()
        ) {
            // Main Content Column
            Column(
                modifier = Modifier
                    .weight(1f)
                    .verticalScroll(rememberScrollState())
                    .padding(16.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                var selectedBoxIndex by remember { mutableStateOf<Int?>(null) }
                val controller = rememberColorPickerController()
                val scope = rememberCoroutineScope()

                Text("Number of colors: $colorCount")

                Slider(
                    value = sliderState,
                    valueRange = 2f..20f,
                    steps = 17,
                    onValueChange = { sliderState = it },
                    modifier = Modifier.width(300.dp)
                )
                
                Text(
                    text = "Click a box to set its color. Once you've set all the colors, press the Randomize Colors button.",
                    style = MaterialTheme.typography.bodyMedium,
                    modifier = Modifier.padding(bottom = 8.dp)
                )

                FlowRow(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(vertical = 10.dp),
                    horizontalArrangement = Arrangement.Center,
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    // Create a list of indices sorted by their assigned number
                    val sortedIndices = (0 until colorCount).sortedBy { index ->
                        assignedNumbers[index] ?: (Int.MAX_VALUE - colorCount + index) // Put unassigned at the end
                    }

                    sortedIndices.forEach { index ->
                        Column(
                            horizontalAlignment = Alignment.CenterHorizontally,
                            modifier = Modifier.padding(horizontal = 4.dp)
                        ) {
                            Box(
                                modifier = Modifier
                                    .size(60.dp)
                                    .padding(4.dp)
                                    .background(colors[index])
                                    .border(
                                        width = if (selectedBoxIndex == index) 3.dp else 0.dp,
                                        color = if (selectedBoxIndex == index) MaterialTheme.colorScheme.onPrimaryContainer else Color.Transparent
                                    )
                                    .clickable {
                                        selectedBoxIndex = index
                                    },
                                contentAlignment = Alignment.Center
                            ) {
                                // Display number if assigned
                                val number = assignedNumbers[index]
                                if (number != null) {
                                    Text(
                                        text = number.toString(),
                                        style = MaterialTheme.typography.bodyLarge,
                                        color = if (colors[index].luminance() > 0.5f) Color.Black else Color.White
                                    )
                                }
                            }
                            
                            // Display row height if assigned
                            val rowHeight = assignedRowHeights[index]
                            if (rowHeight != null) {
                                Text(
                                    text = "Rows: $rowHeight",
                                    style = MaterialTheme.typography.bodySmall,
                                    modifier = Modifier.padding(top = 4.dp)
                                )
                            } else {
                                // Placeholder to maintain height
                                 Text(
                                    text = " ",
                                    style = MaterialTheme.typography.bodySmall,
                                    modifier = Modifier.padding(top = 4.dp)
                                )
                            }
                        }
                    }
                }

                if (selectedBoxIndex != null) {
                    key(selectedBoxIndex) {
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            var expanded by remember { mutableStateOf(false) }
                            val currentManualNumber = manualNumbers[selectedBoxIndex]
                            
                            ExposedDropdownMenuBox(
                                expanded = expanded,
                                onExpandedChange = { expanded = !expanded },
                                modifier = Modifier.padding(8.dp)
                            ) {
                                OutlinedTextField(
                                    value = currentManualNumber?.toString() ?: "Auto",
                                    onValueChange = {},
                                    readOnly = true,
                                    label = { Text("Manual Number") },
                                    trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expanded) },
                                    colors = ExposedDropdownMenuDefaults.outlinedTextFieldColors(),
                                    modifier = Modifier.menuAnchor(ExposedDropdownMenuAnchorType.PrimaryNotEditable, true)
                                )
                                
                                ExposedDropdownMenu(
                                    expanded = expanded,
                                    onDismissRequest = { expanded = false }
                                ) {
                                    DropdownMenuItem(
                                        text = { Text("Auto (Clear)") },
                                        onClick = {
                                            manualNumbers.remove(selectedBoxIndex)
                                            assignedNumbers.remove(selectedBoxIndex)
                                            expanded = false
                                        }
                                    )
                                    
                                    for (i in 1..colorCount) {
                                        // Check if number is already used by another box
                                        val isUsed = manualNumbers.entries.any { (key, value) -> 
                                            value == i && key != selectedBoxIndex 
                                        }
                                        
                                        if (!isUsed) {
                                            DropdownMenuItem(
                                                text = { Text(i.toString()) },
                                                onClick = {
                                                    manualNumbers[selectedBoxIndex!!] = i
                                                    assignedNumbers[selectedBoxIndex!!] = i
                                                    expanded = false
                                                }
                                            )
                                        }
                                    }
                                }
                            }

                            Text("You can manually set colors to a specific number to keep those colors from being randomly assigned.",
                                style = MaterialTheme.typography.bodyMedium,
                                modifier = Modifier.padding(bottom = 5.dp)
                                )

                            HsvColorPicker(
                                modifier = Modifier
                                    .width(300.dp)
                                    .height(200.dp)
                                    .padding(5.dp),
                                controller = controller,
                                initialColor = selectedBoxIndex?.let { colors[it] },
                                onColorChanged = { colorEnvelope: ColorEnvelope ->
                                    selectedBoxIndex?.let { index ->
                                        if (index < colors.size) {
                                            colors[index] = colorEnvelope.color
                                            if (colorEnvelope.fromUser) {
                                                setIndices[index] = true
                                            }
                                        }
                                    }
                                }
                            )
                            
                            BrightnessSlider(
                                modifier = Modifier
                                    .width(300.dp)
                                    .padding(5.dp)
                                    .height(20.dp),
                                controller = controller,
                            )
                        }
                    }
                }

                // Check if all current boxes are set
                val allColorsSet = (0 until colorCount).all { setIndices.containsKey(it) }

                if (allColorsSet) {
                    Button(
                        onClick = {
                            val usedNumbers = manualNumbers.values.toSet()
                            val availableNumbers = (1..colorCount)
                                .filter { it !in usedNumbers }
                                .shuffled()
                                .toMutableList()

                            assignedNumbers.clear()
                            assignedNumbers.putAll(manualNumbers)

                            for (i in 0 until colorCount) {
                                if (!manualNumbers.containsKey(i)) {
                                    if (availableNumbers.isNotEmpty()) {
                                        assignedNumbers[i] = availableNumbers.removeAt(0)
                                    }
                                }
                            }
                        },
                        modifier = Modifier.padding(5.dp)
                    ) {
                        Text("Randomize Colors")
                    }
                    
                    Text("Select Row Heights:", modifier = Modifier.padding(top = 8.dp))
                    LazyRow(
                        modifier = Modifier.fillMaxWidth().padding(5.dp),
                        horizontalArrangement = Arrangement.Center
                    ) {
                        items(10) { index ->
                            val num = index + 1
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                modifier = Modifier.padding(horizontal = 4.dp)
                            ) {
                                Checkbox(
                                    checked = selectedRowHeightOptions[num] == true,
                                    onCheckedChange = { checked ->
                                        if (checked) {
                                            selectedRowHeightOptions[num] = true
                                        } else {
                                            selectedRowHeightOptions.remove(num)
                                        }
                                    }
                                )
                                Text(text = num.toString())
                            }
                        }
                    }
                    
                    if (selectedRowHeightOptions.isNotEmpty()) {
                        Row(
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Button(
                                onClick = {
                                    val options = selectedRowHeightOptions.keys.toList()
                                    for (i in 0 until colorCount) {
                                        assignedRowHeights[i] = options.random()
                                    }
                                }
                            ) {
                                Text("Randomize Row Heights")
                            }
                            
                            Button(
                                onClick = { showStripePreview = !showStripePreview }
                            ) {
                                Text(if (showStripePreview) "Hide Stripe" else "Show Stripe")
                            }
                        }
                    }
                }

                if (assignedNumbers.isNotEmpty()) {
                    Button(
                        onClick = {
                            scope.launch {
                                downloadScreenshot(colors, assignedNumbers, assignedRowHeights, colorCount)
                            }
                        },
                        modifier = Modifier.padding(8.dp)
                    ) {
                        Text("Save Screenshot")
                    }
                }
            }
            
            // Stripe Preview Column
            if (showStripePreview) {
                Column(
                    modifier = Modifier
                        .width(100.dp)
                        .verticalScroll(rememberScrollState())
                        .padding(16.dp),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    Text("Stripe", style = MaterialTheme.typography.titleMedium, modifier = Modifier.padding(bottom = 8.dp))
                    
                    if (assignedNumbers.isNotEmpty() && assignedRowHeights.isNotEmpty()) {
                        // Create a sorted list of (color, rowHeight) based on assigned numbers
                        val sortedStripes = (0 until assignedNumbers.size).mapNotNull { index ->
                            val number = assignedNumbers[index]
                            val height = assignedRowHeights[index]
                            if (number != null && height != null) {
                                Triple(number, colors[index], height)
                            } else null
                        }.sortedBy { it.first }

                        Column(
                            modifier = Modifier
                                .width(60.dp)
                                .border(1.dp, Color.Black)
                        ) {
                            sortedStripes.forEach { (_, color, height) ->
                                Box(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .height((height * 10).dp) // Scale factor: 10dp per row
                                        .background(color)
                                )
                            }
                        }
                    } else {
                        Text("Assign numbers and row heights to see preview", style = MaterialTheme.typography.bodySmall)
                    }
                }
            }
        }
    }
}

expect fun downloadScreenshot(colors: List<Color>, assignedNumbers: Map<Int, Int>, assignedRowHeights: Map<Int, Int>, count: Int)